import { useEffect, useState, useCallback } from 'react';
import type {
  Academy,
  StudentProgramCompletionWithProgramName,
  ProgramCertificateMetadata,
  AcademyCertificateBranding,
} from '@court-sync/shared';
import { programService } from '@/services/program';
import { academyService } from '@/services/academy';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { CertificateDownloadButton } from '@/components/certificates/CertificateDownloadButton';
import { Award, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export interface StudentCertificatesPanelProps {
  academyId: string;
  studentId: string;
  studentFirstName: string;
  studentLastName: string;
  academy: Academy | null;
}

export function StudentCertificatesPanel({
  academyId,
  studentId,
  studentFirstName,
  studentLastName,
  academy,
}: StudentCertificatesPanelProps) {
  const [rows, setRows] = useState<StudentProgramCompletionWithProgramName[]>([]);
  const [loading, setLoading] = useState(true);
  const [certificateBranding, setCertificateBranding] = useState<AcademyCertificateBranding | null>(null);

  useEffect(() => {
    let cancelled = false;
    academyService
      .getCertificateBranding(academyId)
      .then((b) => {
        if (!cancelled) setCertificateBranding(b);
      })
      .catch(() => {
        if (!cancelled) setCertificateBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, [academyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await programService.getStudentProgramCompletions(studentId, academyId);
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, studentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" aria-hidden />
        Loading certificates…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No program pathway completions yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <Card key={row.id}>
          <CardHeader className="pb-2">
            <div className="flex items-start gap-2">
              <Award className="mt-0.5 h-5 w-5 text-amber-600" aria-hidden />
              <div>
                <h3 className="font-semibold">{row.programName}</h3>
                <p className="text-sm text-muted-foreground">
                  Completed {new Date(row.completedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {row.certificateIssued ? (
              <CertificateDownloadButton
                loadCertificate={async () => {
                  const branding =
                    certificateBranding ?? (await academyService.getCertificateBranding(academyId));
                  const meta: ProgramCertificateMetadata = {
                    studentFirstName,
                    studentLastName,
                    programName: row.programName,
                    academyName: academy?.name ?? 'Academy',
                    completedAt: new Date(row.completedAt).toISOString(),
                    academyLogoUrl: branding.logoUrl ?? academy?.logoUrl,
                    academyPrimaryColor: branding.primaryColor ?? academy?.primaryColor,
                    certificateSignerName: branding.signerName,
                    certificateSignerTitle: branding.signerTitle,
                    certificateSignatureImageUrl: branding.signatureImageUrl,
                  };
                  return {
                    meta,
                    filenameBase: `certificate-${row.programName}`,
                  };
                }}
                onError={(e) => toast.error(e instanceof Error ? e.message : 'Could not download')}
                size="sm"
              >
                Download certificate (PDF)
              </CertificateDownloadButton>
            ) : (
              <p className="text-sm text-muted-foreground">
                Certificate will be available here once your academy has issued it.
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
